// English copy for the public "The new Photohawk web uploader" launch page (/web-uploader).
// English is the source of truth: its shape is the WebUploaderCopy type that copy.es.ts and copy.pt.ts
// must match (copy.test.ts). Adapted from the approved launch email (17 Oct 2026). Only these claims:
//  - it sends many photos at once, far more than an FTP app like FileZilla (2 at a time by default, and no
//    more than 10);
//  - it keeps adjusting to make full use of your connection;
//  - built for big events: tested at scale, it comfortably handles uploads of more than 100,000 photos
//    (owner, 17 Oct 2026); no more waiting hours for a big event to go live;
//  - light previews go up first (on every plan since 19 Oct 2026 — the uploader offers it, off by default), so the gallery is
//    live in minutes, not hours, and sells or gives free downloads while the originals follow, and a bought or
//    downloaded photo's original jumps the queue. The one timing figure: a 10,000-photo gallery live in about
//    8 minutes, measured on a 20 Mbps upload (owner, 17 Oct 2026) — always with the 20 Mbps;
//  - it resumes by itself if the connection drops, and adding the folder again sends only what's missing;
//  - live visitors: how many people are viewing your gallery right now, on every plan;
//  - status in plain words: Previews / Originals.
// No other benchmark numbers (no MB/s, %, "x faster", no FTP speeds) and no counts that add previews and
// originals together. The second pass is
// "originals" (never "full-size" / "full-quality"). Never "photographer" or "organiser"; no Wi-Fi cameras
// or tethering. Previews first and on-demand FTP are on every plan: never "Pro and Enterprise".
// Shared FTP closes on 1 November 2026 (SHARED_FTP_RETIRES_ISO in src/content/pricing/plans.ts).

const en = {
  seo: {
    title: "Photohawk web uploader | Faster than FTP for big events",
    description:
      "We've rebuilt the Photohawk web uploader. It sends many photos at once, handles uploads of more than 100,000 photos and resumes by itself if your connection drops, on every plan. On every plan, light previews go up first so your gallery is live in minutes, not hours.",
  },
  hero: {
    badge: "New",
    eyebrow: "On every plan",
    title: "The new Photohawk web uploader",
    subtitle:
      "Faster than FTP, built for uploads of more than 100,000 photos, and it picks up where it left off.",
    /** Shown under the subline, labelled: the eyebrow says "On every plan", previews first is not. */
    plans: "On every plan, previews first gets a big event live in minutes, not hours.",
    ctaPrimary: "Open the web uploader",
    ctaSecondary: "See what's new",
    screenshotAlt:
      "The web uploader mid-upload. With previews first (every plan), the status bar says the gallery is live on previews and shows how many people are visiting it, and the queue on the right shows the previews done and the originals on their way, with two photos people bought going first.",
    phoneAlt: "The web uploader on a phone, open in a folder of the gallery, with the upload status at the bottom.",
  },
  previews: {
    heading: "Previews first",
    /** Shown as a label next to the heading. */
    badge: "Every plan",
    subtitle:
      "On every plan: live in minutes, not hours. A 10,000-photo gallery can be live and selling in about 8 minutes on a 20 Mbps upload.",
    body: "Light previews go up first, so your gallery can go live and start selling, or giving free downloads, while the originals follow. If someone buys or downloads a photo before its original arrives, that original jumps the queue.",
    steps: [
      { id: "previews", text: "Light previews go up first" },
      { id: "live", text: "Your gallery goes live" },
      { id: "buy", text: "Someone buys a photo" },
      { id: "jump", text: "Its original jumps the queue" },
      { id: "originals", text: "The originals follow" },
    ],
    anim: {
      label: "Animated illustration of previews first",
      description:
        "Light previews of six photos fly from your computer into your gallery, and the gallery switches to Live. Someone taps Buy on one photo, and its original moves to the front of the originals queue and goes up first. The other originals follow.",
      computer: "Your computer",
      gallery: "Your gallery",
      notLive: "Not live yet",
      live: "Live",
      buy: "Buy",
      bought: "Bought",
      queue: "Originals",
      goesFirst: "Goes first",
    },
    link: "How preview-first uploads work",
  },
  faster: {
    heading: "Why it's faster than FTP",
    subtitle: "The same photos, sent two ways.",
    anim: {
      label: "Animated comparison of an FTP app and the web uploader",
      description:
        "Two rows. In the top row, an FTP app sends photos 2 at a time by default, and no more than 10. In the bottom row, the web uploader sends many photos at once in lanes that widen and narrow to fit the connection. On every plan, light previews go first, so its gallery goes live sooner. The connection drops for a moment and the upload resumes by itself.",
      ftp: "FTP app",
      ftpNote: "2 at a time by default",
      ftpMax: "up to 10 at most",
      web: "Web uploader",
      webNote: "Many at once",
      adapts: "Adapts to your bandwidth",
      gallery: "Gallery",
      live: "Live",
      dropped: "Connection dropped",
      resumed: "Resumed by itself",
      legendPreviews: "Previews",
      legendOriginals: "Originals",
    },
    points: [
      {
        id: "parallel",
        title: "Many photos at once",
        body: "It sends many photos at once, far more than an FTP app like FileZilla (2 at a time by default, and no more than 10).",
      },
      {
        id: "adaptive",
        title: "Makes full use of your connection",
        body: "It keeps adjusting as it uploads, to make full use of your connection.",
      },
      {
        id: "scale",
        title: "Built for big events",
        body: "We've tested it at scale, and it comfortably handles uploads of more than 100,000 photos.",
      },
      {
        id: "live",
        title: "Live in minutes, not hours (every plan)",
        body: "On every plan, light previews go up first, so a 10,000-photo gallery can be live and selling in about 8 minutes on a 20 Mbps upload, while the originals follow.",
      },
      {
        id: "resume",
        title: "Picks up where it left off",
        body: "If your connection drops, it resumes by itself. Add the folder again and only what's missing is sent.",
      },
    ],
  },
  /** The upload-time estimator (#estimate). {placeholders} are filled in by the page. */
  estimate: {
    heading: "How long will your upload take?",
    subtitle: "An estimate for your gallery, from your own upload speed.",
    heroLink: "Estimate your upload time",
    photos: "Number of photos",
    custom: "Other",
    customLabel: "Number of photos (other)",
    size: "Average size of an original",
    sizeUnit: "MB",
    sizeHint: "Event JPEGs are often around {mb} MB.",
    speed: "Your upload speed",
    speedUnit: "Mbps",
    test: "Test my upload speed",
    testing: "Testing… {mbps} Mbps",
    testStarting: "Testing…",
    testDone: "Measured: {mbps} Mbps upload",
    testFailed: "We couldn't measure your speed just now. Enter it yourself, or try a general speed test.",
    dataNote: "Uses up to about {mb} MB of data, and only when you tap the button.",
    otherTest: "Or use a general speed test",
    resultsHeading: "Your estimate",
    previewsLabel: "Previews first",
    previewsPlans: "Every plan",
    previewsLive: "Gallery live in {time}",
    previewsAll: "All originals uploaded in {time}",
    originalsLabel: "Originals only",
    originalsLive: "Gallery live in {time}",
    originalsWhen: "when all {gb} GB of originals is up",
    computerNote: "At this speed, how quickly your computer makes the previews sets the pace.",
    disclaimer: "Estimates only. Real times depend on your connection, your computer and your photos.",
    time: {
      under: "under a minute",
      min: "about {n} min",
      h: "about {n} h",
      d: "about {n} days",
    },
    table: {
      caption: "Example: 10,000 photos",
      speed: "Upload speed",
      live: "Previews first: gallery live (every plan)",
      all: "All originals uploaded",
      note: "The figure of about 8 minutes was measured on a 20 Mbps upload (previews first). The other rows are estimates from the same model, with originals of about {mb} MB.",
    },
  },
  features: {
    heading: "More that's new",
    subtitle: "Real screens from the new web uploader.",
    items: [
      {
        id: "status",
        title: "Status in plain words",
        body: "See where things stand at a glance. With previews first (every plan), that's Previews, then Originals, each with its own progress.",
        alt: "The upload status with previews first (every plan): “Live — saving originals…”, one progress row for Previews and one for Originals, and “2 photos people bought go first”.",
      },
      {
        id: "queue",
        title: "A queue that goes where you want it",
        body: "Dock the upload queue on the right, on the left or along the bottom, and drag it to the size you like. Your browser remembers your choice.",
        alt: "The upload queue docked along the bottom of the uploader, with its menu open to move it to the right, the bottom or the left.",
      },
      {
        id: "visitors",
        title: "See who's looking",
        body: "The status bar shows how many people are viewing your gallery right now, on every plan.",
        alt: "The status bar at the top of the uploader: “Live on previews” (with previews first) and the number of visitors.",
      },
      {
        id: "resume",
        title: "Picks up where it left off",
        body: "If your connection drops, it resumes by itself. Add the folder again and only what's missing is sent.",
        alt: "A banner in the uploader saying a folder didn't finish uploading, with a Resume button.",
      },
      {
        id: "phone",
        title: "Clear on a phone, too",
        body: "On a small screen the folder path folds up to fit, and tapping “…” shows the folders in between, so you always know where your photos are going.",
        alt: "The uploader on a phone: the folder path shows the current folder, and “…” opens a list of the folders above it.",
      },
    ],
  },
  ftp: {
    /** Shown as a date badge. */
    date: "1 November 2026",
    heading: "What's changing with FTP on 1 November",
    body: "Our always-on shared FTP servers close on 1 November 2026. Using FTP today? Please switch before then.",
    points: [
      {
        id: "closes",
        title: "Shared FTP closes on 1 November 2026",
        body: "The always-on shared FTP servers switch off on 1 November 2026.",
      },
      {
        id: "standard",
        title: "The web uploader is the standard way to upload",
        body: "It's on every plan, and you can use it today.",
      },
      {
        id: "ondemand",
        title: "If FTP is essential",
        body: "On every plan, everyone in your workspace can start their own on-demand FTP server from the web uploader, at a personal address that never changes. It switches off after an hour without activity.",
      },
    ],
    helpLink: "How on-demand FTP works",
  },
  more: {
    previewFirstTitle: "How preview-first uploads work",
    previewFirstBody: "On every plan. Step by step: previews, going live, and what buyers see.",
  },
  cta: {
    heading: "Try it on your next upload",
    body: "The new web uploader is ready on every plan. Add a folder and watch your gallery go live.",
    button: "Open the web uploader",
  },
  /** The animations' pause control. */
  pause: "Pause animation",
  play: "Play animation",
};

export type WebUploaderCopy = typeof en;
export default en;
